import { CameraOutlined, CheckCircleOutlined, DeleteOutlined, PlayCircleOutlined, UploadOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Empty,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Space,
  Table,
  Tag,
  Timeline,
  Typography,
  Upload,
} from "antd";
import { useEffect, useRef, useState } from "react";
import { api, unwrap } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { RefreshButton } from "@/components/RefreshButton";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n, type MessageKey } from "@/i18n";
import { SkuSelect } from "../catalog/mapping";
import { loadPackingVideo } from "../warehouse/agent";
import { VideoOnDevice } from "../warehouse/VideoOnDevice";
import { RespondDeadline, expectsGoods, type Case } from "./common";

type Evidence = Case["evidence"][number];
type Source = Evidence["source"];

const MAX_REJECT_PICTURES = 5;

/** Sends a picture to the case (a frame from a video, or a photo). */
async function uploadEvidence(caseId: string, blob: Blob, source: Source, caption: string) {
  return unwrap(
    api.POST("/v1/aftersales/{caseID}/evidence", {
      params: { path: { caseID: caseId }, query: { source, caption } },
      body: blob as unknown as string,
      bodySerializer: (b: unknown) => b as Blob,
      headers: { "Content-Type": blob.type },
    }),
  );
}

/** The frame on screen as a JPEG, with what it is burnt in at the bottom. */
function captureFrame(video: HTMLVideoElement, stamp: string): Promise<Blob> {
  const w = video.videoWidth || 1280;
  const h = video.videoHeight || 720;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const g = canvas.getContext("2d")!;
  g.drawImage(video, 0, 0, w, h);
  const size = Math.max(14, Math.round(h / 32));
  g.fillStyle = "rgba(0,0,0,0.6)";
  g.fillRect(0, h - size * 2, w, size * 2);
  g.fillStyle = "white";
  g.font = `${size}px sans-serif`;
  g.textBaseline = "middle";
  g.fillText(stamp, size * 0.6, h - size);
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("capture"))), "image/jpeg", 0.9));
}

function clock(seconds: number) {
  const s = Math.floor(seconds);
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

/**
 * A recording fetched on demand (it can be large), with "Ambil foto bukti":
 * the frame on screen becomes a picture of the case, stamped with the order,
 * the recording and the moment in it.
 */
export function EvidenceVideo({
  load,
  caseId,
  source,
  stamp,
  canCapture,
}: {
  load: () => Promise<Blob>;
  caseId: string;
  source: Exclude<Source, "upload">;
  /** What the picture says it shows, before the moment in the video. */
  stamp: string;
  canCapture: boolean;
}) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const ref = useRef<HTMLVideoElement>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [capturing, setCapturing] = useState(false);
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url],
  );
  const play = async () => {
    setLoading(true);
    try {
      setUrl(URL.createObjectURL(await load()));
    } catch (err) {
      void message.error(errorText(err));
    } finally {
      setLoading(false);
    }
  };
  const capture = async () => {
    const v = ref.current;
    if (!v) return;
    v.pause();
    setCapturing(true);
    try {
      const at = clock(v.currentTime);
      const blob = await captureFrame(v, `${stamp} · ${at}`);
      await uploadEvidence(caseId, blob, source, `${t(`as.ev.source.${source}`)} ${at}`);
      void queryClient.invalidateQueries({ queryKey: ["aftersales", "case", caseId] });
      void message.success(t("as.ev.captured", { at }));
    } catch (err) {
      void message.error(errorText(err));
    } finally {
      setCapturing(false);
    }
  };
  if (!url) {
    return (
      <Button icon={<PlayCircleOutlined />} loading={loading} onClick={() => void play()}>
        {t(source === "packing_video" ? "orders.playVideo" : "as.playVideo")}
      </Button>
    );
  }
  return (
    <Space orientation="vertical" size={8} style={{ width: "100%" }}>
      <video ref={ref} src={url} controls autoPlay style={{ width: "100%", borderRadius: 4, background: "black" }} />
      {canCapture && (
        <Space wrap>
          <Button icon={<CameraOutlined />} loading={capturing} onClick={() => void capture()}>
            {t("as.ev.capture")}
          </Button>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t("as.ev.captureHelp")}
          </Typography.Text>
        </Space>
      )}
    </Space>
  );
}

/**
 * Bukti packing: how the warehouse picked and packed the order, and its
 * packing video, kept while the case needs it, with frames taken as evidence.
 */
export function PackingEvidence({ c, canCapture }: { c: Case; canCapture: boolean }) {
  const { t, formatDateTime, formatNumber } = useI18n();
  const { me, can } = useAuth();
  const tz = me?.workspace?.timezone;
  const orderId = c.order?.id ?? "";
  const parcel = useQuery({
    queryKey: ["packing", "order", orderId],
    queryFn: () => unwrap(api.GET("/v1/packing/orders/{orderID}", { params: { path: { orderID: orderId } } })),
    enabled: !!orderId,
  });
  const order = useQuery({
    queryKey: ["orders", "detail", orderId],
    queryFn: () => unwrap(api.GET("/v1/orders/{orderID}", { params: { path: { orderID: orderId } } })),
    enabled: !!orderId && can("orders.view"),
  });
  if (!c.order) return null;
  const p = parcel.data?.parcel;
  const steps = (order.data?.steps ?? []).filter((s) => s.kind === "pick_started" || s.kind === "picked" || s.kind === "packed");
  return (
    <Card size="small" title={t("as.ev.packing")}>
      {!p ? (
        <Typography.Text type="secondary">{parcel.isPending ? "…" : t("as.ev.noParcel")}</Typography.Text>
      ) : (
        <Space orientation="vertical" size={12} style={{ width: "100%" }}>
          <Descriptions column={{ xs: 1, md: 2 }} size="small">
            <Descriptions.Item label={t("orders.parcelPacked")}>
              {p.packedBy} · {formatDateTime(p.packedAt, tz)}
            </Descriptions.Item>
            {p.deskName && (
          <Descriptions.Item label={t("wms.desk.packedAt")}>
            {p.deskName}
            {p.startedAt && ` · ${formatDateTime(p.startedAt, tz)} – ${formatDateTime(p.packedAt, tz)}`}
          </Descriptions.Item>
        )}
            <Descriptions.Item label={t("wms.pack.box")}>
              <span className="z-num">{p.packagingCode || "—"}</span> · {formatNumber(p.weightG)} g
            </Descriptions.Item>
            {steps.map((s) => (
              <Descriptions.Item key={s.kind + s.at} label={t(`orders.step.${s.kind}`)}>
                {s.actor || "—"} · {formatDateTime(s.at, tz)}
              </Descriptions.Item>
            ))}
          </Descriptions>
          {p.video ? (
            <Space orientation="vertical" size={4} style={{ width: "100%" }}>
              <EvidenceVideo
                caseId={c.id}
                source="packing_video"
                canCapture={canCapture}
                stamp={`${c.order.number} · ${t("as.ev.source.packing_video")} ${formatDateTime(p.video.recordedAt, tz)}`}
                load={() => loadPackingVideo(orderId, p.video?.location)}
              />
              {p.video.location === "device" && <VideoOnDevice orderId={orderId} requested={p.video.uploadRequested} />}
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {p.video.heldUntil
                  ? t("as.ev.heldUntil", { at: formatDateTime(p.video.heldUntil, tz) })
                  : t("orders.videoKept", { at: formatDateTime(p.video.expiresAt, tz) })}
              </Typography.Text>
            </Space>
          ) : (
            <Tag>{t("orders.noVideo")}</Tag>
          )}
        </Space>
      )}
    </Card>
  );
}

/** A picture of the case, fetched with the session (not a public URL). */
function EvidenceThumb({ caseId, e, size = 96 }: { caseId: string; e: Evidence; size?: number }) {
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    let live = true;
    let made: string | undefined;
    void unwrap(api.GET("/v1/aftersales/{caseID}/evidence/{evidenceID}", { params: { path: { caseID: caseId, evidenceID: e.id } }, parseAs: "blob" })).then(
      (b) => {
        made = URL.createObjectURL(b);
        if (live) setUrl(made);
      },
      () => undefined,
    );
    return () => {
      live = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [caseId, e.id]);
  return <Image src={url} width={size} height={Math.round((size * 3) / 4)} style={{ objectFit: "cover", borderRadius: 4, background: "var(--ant-color-fill-tertiary)" }} />;
}

/** Bukti foto: frames from the videos and photos, sent with a rejection. */
export function EvidenceCard({ c, editable }: { c: Case; editable: boolean }) {
  const { t, formatDateTime } = useI18n();
  const { me } = useAuth();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const tz = me?.workspace?.timezone;
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["aftersales", "case", c.id] });
  const remove = useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE("/v1/aftersales/{caseID}/evidence/{evidenceID}", { params: { path: { caseID: c.id, evidenceID: id } } })),
    onSuccess: refresh,
    onError: (err) => void message.error(errorText(err)),
  });
  const upload = useMutation({
    mutationFn: (f: File) => uploadEvidence(c.id, f, "upload", f.name.replace(/\.[a-z]+$/i, "").slice(0, 200)),
    onSuccess: refresh,
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Card
      size="small"
      title={t("as.ev.title")}
      extra={
        editable && (
          <Upload
            accept="image/jpeg,image/png"
            showUploadList={false}
            beforeUpload={(f) => {
              upload.mutate(f);
              return false;
            }}
          >
            <Button size="small" icon={<UploadOutlined />} loading={upload.isPending}>
              {t("as.ev.upload")}
            </Button>
          </Upload>
        )
      }
    >
      {c.evidence.length === 0 ? (
        <Typography.Text type="secondary">{t("as.ev.empty")}</Typography.Text>
      ) : (
        <Image.PreviewGroup>
          <Space wrap size={12} align="start">
            {c.evidence.map((e) => (
              <Space key={e.id} orientation="vertical" size={2} style={{ width: 120 }}>
                <EvidenceThumb caseId={c.id} e={e} size={120} />
                <Typography.Text style={{ fontSize: 12 }} ellipsis={{ tooltip: e.caption }}>
                  {e.caption || t(`as.ev.source.${e.source}`)}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  {e.createdName} · {formatDateTime(e.createdAt, tz)}
                </Typography.Text>
                {e.sentAt ? (
                  <Tag color="success" icon={<CheckCircleOutlined />}>
                    {t("as.ev.sent")}
                  </Tag>
                ) : (
                  editable && (
                    <Button
                      size="small"
                      type="link"
                      danger
                      icon={<DeleteOutlined />}
                      style={{ padding: 0 }}
                      onClick={() =>
                        modal.confirm({
                          title: t("as.ev.remove"),
                          okButtonProps: { danger: true },
                          okText: t("common.delete"),
                          cancelText: t("common.cancel"),
                          onOk: () => remove.mutateAsync(e.id),
                        })
                      }
                    >
                      {t("common.delete")}
                    </Button>
                  )
                )}
              </Space>
            ))}
          </Space>
        </Image.PreviewGroup>
      )}
    </Card>
  );
}

type RejectValues = { reason: string; comment?: string; evidenceIds?: string[] };

/**
 * Jawab ke marketplace: what the platform waits for (the request, or the
 * returned parcel), before its deadline. Every answer says what it does to
 * the money and the goods first.
 */
export function AnswerCard({ c, onDone }: { c: Case; onDone: (c: Case) => void }) {
  const { t, formatMoney } = useI18n();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const [rejecting, setRejecting] = useState(false);
  const [partial, setPartial] = useState(false);
  const [rejectForm] = Form.useForm<RejectValues>();
  const [partialForm] = Form.useForm<{ amount: number }>();
  const receipt = c.respondAction === "receipt";
  const reasons = useQuery({
    queryKey: ["aftersales", "reject-reasons", c.id, c.respondAction],
    queryFn: () => unwrap(api.GET("/v1/aftersales/{caseID}/reject-reasons", { params: { path: { caseID: c.id } } })),
    enabled: rejecting,
    staleTime: 60_000,
  });
  const respond = useMutation({
    mutationFn: (body: { answer: "approve" | "reject" | "partial_refund"; reason?: string; comment?: string; evidenceIds?: string[]; amount?: number }) =>
      unwrap(api.POST("/v1/aftersales/{caseID}/respond", { params: { path: { caseID: c.id } }, body })),
    onSuccess: (x) => {
      setRejecting(false);
      setPartial(false);
      onDone(x);
      void message.success(t("as.answer.sent"));
    },
    onError: (err) => {
      if (!(rejecting && applyFieldErrors(rejectForm, err)) && !(partial && applyFieldErrors(partialForm, err))) void message.error(errorText(err));
    },
  });
  const refund = c.refundRequested != null ? formatMoney(c.refundRequested) : "—";
  const goodsPending = receipt && expectsGoods(c.kind) && c.status === "open";
  const approveEffect = receipt
    ? t("as.answer.effect.receipt", { amount: refund })
    : c.kind === "refund_only"
      ? t("as.answer.effect.refund", { amount: refund })
      : c.kind === "exchange"
        ? t("as.answer.effect.exchange")
        : t("as.answer.effect.return");
  const approve = () =>
    modal.confirm({
      title: t(receipt ? "as.answer.approveReceipt" : "as.answer.approve"),
      width: 520,
      content: (
        <Space orientation="vertical">
          <Typography.Text>{approveEffect}</Typography.Text>
          {goodsPending && <Alert type="warning" showIcon title={t("as.answer.goodsNotChecked")} />}
        </Space>
      ),
      okText: t(receipt ? "as.answer.approveReceipt" : "as.answer.approve"),
      cancelText: t("common.cancel"),
      onOk: () => respond.mutateAsync({ answer: "approve" }),
    });
  return (
    <Card size="small" title={t("as.answer.title")} extra={<RespondDeadline at={c.respondBy} />} styles={{ header: { background: "var(--ant-color-error-bg)" } }}>
      <Space orientation="vertical" size={12} style={{ width: "100%" }}>
        <Typography.Text>{t(receipt ? "as.answer.askReceipt" : `as.answer.ask.${c.kind === "refund_only" ? "refund" : "return"}`)}</Typography.Text>
        <Space wrap>
          <Button type="primary" onClick={approve} loading={respond.isPending}>
            {t(receipt ? "as.answer.approveReceipt" : "as.answer.approve")}
          </Button>
          <Button danger onClick={() => setRejecting(true)}>
            {t(receipt ? "as.answer.rejectReceipt" : "as.answer.reject")}
          </Button>
          {!receipt && <Button onClick={() => setPartial(true)}>{t("as.answer.partial")}</Button>}
        </Space>
        {c.evidence.length === 0 && <Typography.Text type="secondary" style={{ fontSize: 12 }}>{t("as.answer.evidenceHint")}</Typography.Text>}
      </Space>

      <Modal
        open={rejecting}
        title={t(receipt ? "as.answer.rejectReceipt" : "as.answer.reject")}
        onCancel={() => setRejecting(false)}
        onOk={() => rejectForm.submit()}
        okText={t("as.answer.send")}
        okButtonProps={{ danger: true }}
        cancelText={t("common.cancel")}
        confirmLoading={respond.isPending}
        width={640}
        destroyOnHidden
        afterClose={() => rejectForm.resetFields()}
      >
        <Form
          form={rejectForm}
          layout="vertical"
          requiredMark={false}
          initialValues={{ evidenceIds: c.evidence.slice(0, MAX_REJECT_PICTURES).map((e) => e.id) }}
          onFinish={(v) => respond.mutate({ answer: "reject", reason: v.reason, comment: v.comment ?? "", evidenceIds: v.evidenceIds ?? [] })}
        >
          <Typography.Paragraph type="secondary">{t("as.answer.rejectEffect")}</Typography.Paragraph>
          <Form.Item name="reason" label={t("as.answer.reason")} rules={[{ required: true }]}>
            <Select loading={reasons.isFetching} options={(reasons.data?.items ?? []).map((r) => ({ value: r.name, label: r.text }))} />
          </Form.Item>
          <Form.Item name="comment" label={t("as.answer.comment")} extra={t("as.answer.commentHelp")}>
            <Input.TextArea rows={3} maxLength={500} showCount />
          </Form.Item>
          <Form.Item
            name="evidenceIds"
            label={t("as.answer.pictures", { n: MAX_REJECT_PICTURES })}
            rules={[{ validator: (_, v?: string[]) => ((v?.length ?? 0) > MAX_REJECT_PICTURES ? Promise.reject(new Error(t("as.answer.tooMany", { n: MAX_REJECT_PICTURES }))) : Promise.resolve()) }]}
          >
            {c.evidence.length === 0 ? (
              <Alert type="warning" showIcon title={t("as.answer.noPictures")} />
            ) : (
              <Checkbox.Group style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                {c.evidence.map((e) => (
                  <Checkbox key={e.id} value={e.id}>
                    <Space orientation="vertical" size={2}>
                      <EvidenceThumb caseId={c.id} e={e} size={96} />
                      <Typography.Text style={{ fontSize: 11, maxWidth: 96 }} ellipsis>
                        {e.caption || t(`as.ev.source.${e.source}`)}
                      </Typography.Text>
                    </Space>
                  </Checkbox>
                ))}
              </Checkbox.Group>
            )}
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={partial}
        title={t("as.answer.partial")}
        onCancel={() => setPartial(false)}
        onOk={() => partialForm.submit()}
        okText={t("as.answer.send")}
        cancelText={t("common.cancel")}
        confirmLoading={respond.isPending}
        destroyOnHidden
        afterClose={() => partialForm.resetFields()}
      >
        <Form form={partialForm} layout="vertical" requiredMark={false} onFinish={(v) => respond.mutate({ answer: "partial_refund", amount: v.amount })}>
          <Typography.Paragraph type="secondary">{t("as.answer.partialEffect", { amount: refund })}</Typography.Paragraph>
          <Form.Item name="amount" label={t("as.refundAmount")} rules={[{ required: true }]}>
            <InputNumber min={1} max={c.refundRequested != null ? c.refundRequested - 1 : undefined} precision={0} prefix="Rp" style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}

/** Riwayat di marketplace: the request's negotiation, read live from the platform. */
export function MarketplaceRecords({ c }: { c: Case }) {
  const { t, formatDateTime } = useI18n();
  const { me } = useAuth();
  const errorText = useErrorText();
  const tz = me?.workspace?.timezone;
  const q = useQuery({
    queryKey: ["aftersales", "records", c.id],
    queryFn: () => unwrap(api.GET("/v1/aftersales/{caseID}/records", { params: { path: { caseID: c.id } } })),
    enabled: c.canAnswer,
  });
  if (!c.canAnswer) return null;
  const roles: Record<string, { color: string; label: MessageKey }> = {
    BUYER: { color: "blue", label: "as.records.role.BUYER" },
    SELLER: { color: "green", label: "as.records.role.SELLER" },
    PLATFORM: { color: "purple", label: "as.records.role.PLATFORM" },
    SYSTEM: { color: "default", label: "as.records.role.SYSTEM" },
  };
  return (
    <Card size="small" title={t("as.records.title")} extra={<RefreshButton queryKeys={[["aftersales", "records", c.id]]} />}>
      {q.isError ? (
        <Typography.Text type="danger">{errorText(q.error)}</Typography.Text>
      ) : !q.data ? (
        <Typography.Text type="secondary">…</Typography.Text>
      ) : q.data.items.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Timeline
          items={q.data.items.map((r, i) => ({
            key: i,
            content: (
              <Space orientation="vertical" size={0}>
                <Space size={6} wrap>
                  <Tag color={roles[r.role]?.color ?? "default"}>{r.role in roles ? t(roles[r.role]!.label) : r.role}</Tag>
                  <Typography.Text>{r.description || r.event}</Typography.Text>
                </Space>
                {(r.reason || r.note) && <Typography.Text type="secondary">{[r.reason, r.note].filter(Boolean).join(" · ")}</Typography.Text>}
                {r.images.length > 0 && (
                  <Image.PreviewGroup>
                    <Space wrap size={4}>
                      {r.images.map((src) => (
                        <Image key={src} src={src} width={64} height={48} style={{ objectFit: "cover", borderRadius: 4 }} />
                      ))}
                    </Space>
                  </Image.PreviewGroup>
                )}
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {formatDateTime(r.at, tz)}
                </Typography.Text>
              </Space>
            ),
          }))}
        />
      )}
    </Card>
  );
}

type Scenario = "wrong_item" | "short" | "over";
type CorrectionValues = { scenario: Scenario; ordered?: string; actual?: string; quantity: number; note: string };

/**
 * Koreksi pengiriman: the parcel did not hold what was booked (the packing
 * video shows it). Stock and the cost of goods sold are corrected; goods
 * still coming back follow what actually left.
 */
export function CorrectionCard({ c, editable, onDone }: { c: Case; editable: boolean; onDone: (c: Case) => void }) {
  const { t, formatDateTime } = useI18n();
  const { me } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const tz = me?.workspace?.timezone;
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<CorrectionValues>();
  const scenario = Form.useWatch("scenario", form);
  const ordered = Form.useWatch("ordered", form);
  const shipped = new Map<string, { code: string; name: string; quantity: number; orderLineId: string }>();
  for (const s of c.shipped) {
    const cur = shipped.get(s.skuId);
    shipped.set(s.skuId, { code: s.skuCode, name: s.skuName, quantity: (cur?.quantity ?? 0) + s.quantity, orderLineId: cur?.orderLineId ?? s.orderLineId });
  }
  const save = useMutation({
    mutationFn: (v: CorrectionValues) => {
      const q = v.quantity;
      const line = v.ordered ? shipped.get(v.ordered) : undefined;
      const lines =
        v.scenario === "wrong_item"
          ? [
              { skuId: v.ordered!, quantity: q },
              { skuId: v.actual!, quantity: -q, orderLineId: line?.orderLineId ?? null },
            ]
          : v.scenario === "short"
            ? [{ skuId: v.ordered!, quantity: q }]
            : [{ skuId: v.actual!, quantity: -q, orderLineId: c.shipped[0]?.orderLineId ?? null }];
      return unwrap(api.POST("/v1/aftersales/{caseID}/correction", { params: { path: { caseID: c.id } }, body: { note: v.note.trim(), lines } }));
    },
    onSuccess: (x) => {
      setOpen(false);
      onDone(x);
      void message.success(t("as.fix.saved"));
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  if (!c.order || (c.shipped.length === 0 && !c.correction)) return null;
  const canFix = editable && !c.correction && c.status !== "cancelled" && c.status !== "receiving" && c.shipped.length > 0;
  return (
    <Card
      size="small"
      title={t("as.fix.title")}
      extra={
        canFix && (
          <Button size="small" onClick={() => setOpen(true)}>
            {t("as.fix.open")}
          </Button>
        )
      }
    >
      <Space orientation="vertical" size={8} style={{ width: "100%" }}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t("as.fix.shippedHelp")}
        </Typography.Text>
        <Table
          size="small"
          rowKey="skuId"
          pagination={false}
          dataSource={[...shipped.entries()].map(([skuId, s]) => ({ skuId, ...s }))}
          columns={[
            {
              title: t("as.item"),
              render: (_, s) => (
                <span>
                  <Typography.Text strong className="z-num">
                    {s.code}
                  </Typography.Text>
                  <Typography.Text type="secondary"> · {s.name}</Typography.Text>
                </span>
              ),
            },
            { title: t("as.fix.shipped"), dataIndex: "quantity", width: 110, align: "right" },
          ]}
        />
        {c.correction && (
          <Alert
            type="info"
            showIcon
            title={t("as.fix.done", { by: c.correction.by, at: formatDateTime(c.correction.at, tz) })}
            description={
              <Space orientation="vertical" size={2}>
                {c.correction.lines.map((l) => (
                  <Typography.Text key={l.skuId}>
                    <span className="z-num">{l.skuCode}</span> · {t(l.quantity > 0 ? "as.fix.back" : "as.fix.out", { n: Math.abs(l.quantity) })}
                  </Typography.Text>
                ))}
                <Typography.Text type="secondary">{c.correction.note}</Typography.Text>
              </Space>
            }
          />
        )}
      </Space>
      <Modal
        open={open}
        title={t("as.fix.title")}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        okText={t("as.fix.save")}
        cancelText={t("common.cancel")}
        confirmLoading={save.isPending}
        width={620}
        destroyOnHidden
        afterClose={() => form.resetFields()}
      >
        <Form form={form} layout="vertical" requiredMark={false} initialValues={{ scenario: "wrong_item", quantity: 1 }} onFinish={(v) => save.mutate(v)}>
          <Form.Item name="scenario" label={t("as.fix.scenario")}>
            <Radio.Group
              optionType="button"
              options={(["wrong_item", "short", "over"] as const).map((s) => ({ value: s, label: t(`as.fix.scenario.${s}`) }))}
            />
          </Form.Item>
          <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
            {t(`as.fix.help.${scenario ?? "wrong_item"}`)}
          </Typography.Paragraph>
          {scenario !== "over" && (
            <Form.Item name="ordered" label={t(scenario === "short" ? "as.fix.shortSku" : "as.fix.orderedSku")} rules={[{ required: true }]}>
              <Select
                options={[...shipped.entries()].map(([id, s]) => ({
                  value: id,
                  label: (
                    <span>
                      <span className="z-num">{s.code}</span> · {s.name} ({s.quantity})
                    </span>
                  ),
                }))}
              />
            </Form.Item>
          )}
          {scenario !== "short" && (
            <Form.Item name="actual" label={t(scenario === "over" ? "as.fix.extraSku" : "as.fix.actualSku")} rules={[{ required: true }]}>
              <SkuSelect singleOnly />
            </Form.Item>
          )}
          <Form.Item name="quantity" label={t("as.dock.quantity")} rules={[{ required: true }]}>
            <InputNumber min={1} max={scenario !== "over" && ordered ? shipped.get(ordered)?.quantity : 1000} precision={0} style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="note" label={t("as.fix.note")} extra={t("as.fix.noteHelp")} rules={[{ required: true, whitespace: true }]}>
            <Input.TextArea rows={2} maxLength={500} showCount />
          </Form.Item>
          <Alert type="warning" showIcon title={t("as.fix.effect")} />
        </Form>
      </Modal>
    </Card>
  );
}
